import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api.ts';
import { useTenantSlug } from './tenant.ts';

export interface Branding { tenantName: string; displayName: string | null; accent: string | null; header: string | null; logoUrl: string | null; /** CR-040 랜덤 조직 ID (공개 URL 접두어) */ publicId?: string }

/** 테넌트 브랜딩 (CR-019). 로그인 전에도 조회되는 공개 API. 강조색은 CSS 변수로 적용한다 */
export function useBranding() {
  const slug = useTenantSlug();
  const q = useQuery({ queryKey: ['branding', slug], queryFn: () => api<Branding>('/tenant/branding'), enabled: !!slug, staleTime: 5 * 60_000, retry: false });
  useEffect(() => { applyAccent(q.data?.accent ?? null); }, [q.data?.accent]);
  useEffect(() => { applyHeader(q.data?.header ?? null); }, [q.data?.header]);
  return q.data ?? null;
}

const hex = (c: string) => [1, 3, 5].map((i) => parseInt(c.slice(i, i + 2), 16));
const mix = (a: number[], b: number[], t: number) => '#' + a.map((v, i) => Math.round(v + (b[i] - v) * t).toString(16).padStart(2, '0')).join('');
/** 상대 휘도(sRGB). 0.5 이상이면 밝은 색으로 보고 어두운 글자를 쓴다 */
function luminance(c: number[]) { const f = (v: number) => { const x = v / 255; return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4; }; return 0.2126 * f(c[0]) + 0.7152 * f(c[1]) + 0.0722 * f(c[2]); }
export function isLightColor(color: string | null | undefined) { return !!color && /^#[0-9a-f]{6}$/i.test(color) && luminance(hex(color)) >= 0.5; }
/** 같은 색상(hue)·채도로 밝기만 바꾼다 (그라데이션 끝 색) */
function lighten(c: number[], dl: number) {
  const [r, g, b] = c.map((v) => v / 255); const max = Math.max(r, g, b), min = Math.min(r, g, b); let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) { const d = max - min; s = l > 0.5 ? d / (2 - max - min) : d / (max + min); h = max === r ? ((g - b) / d + (g < b ? 6 : 0)) : max === g ? (b - r) / d + 2 : (r - g) / d + 4; h /= 6; }
  const l2 = Math.min(0.95, Math.max(0.05, l + dl));
  const q = l2 < 0.5 ? l2 * (1 + s) : l2 + s - l2 * s, p = 2 * l2 - q;
  const t2c = (t: number) => { if (t < 0) t += 1; if (t > 1) t -= 1; if (t < 1 / 6) return p + (q - p) * 6 * t; if (t < 1 / 2) return q; if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6; return p; };
  return '#' + [t2c(h + 1 / 3), t2c(h), t2c(h - 1 / 3)].map((v) => Math.round(v * 255).toString(16).padStart(2, '0')).join('');
}
/** CR-026: 상단 메뉴바 색. 로고 영역은 40% 어둡게, 오른쪽 그라데이션 끝은 밝기 +14%, 글자는 배경 밝기에 따라 흰/검. 비우면 전역 기본(#0a2540) */
export function applyHeader(header: string | null) {
  const root = document.documentElement.style;
  if (!header || !/^#[0-9a-f]{6}$/i.test(header)) { for (const k of ['gnb', 'gnb-deep', 'gnb-light', 'gnb-fg']) root.removeProperty(`--color-${k}`); return; }
  const c = hex(header), light = isLightColor(header);
  // 밝은 색은 로고 영역을 조금만(12%) 어둡게 — 40% 면 탁한 회색이 된다
  root.setProperty('--color-gnb', header); root.setProperty('--color-gnb-deep', mix(c, [0, 0, 0], light ? 0.12 : 0.4)); root.setProperty('--color-gnb-light', lighten(c, 0.14));
  root.setProperty('--color-gnb-fg', light ? '#0f172a' : '#ffffff');
}
export function applyAccent(accent: string | null) {
  const root = document.documentElement.style;
  if (!accent || !/^#[0-9a-f]{6}$/i.test(accent)) { for (const k of ['accent', 'accent-strong', 'accent-soft', 'accent-muted']) root.removeProperty(`--color-${k}`); return; }
  const c = hex(accent), black = [0, 0, 0], white = [255, 255, 255];
  root.setProperty('--color-accent', accent); root.setProperty('--color-accent-strong', mix(c, black, 0.3)); root.setProperty('--color-accent-soft', mix(c, white, 0.88)); root.setProperty('--color-accent-muted', mix(c, white, 0.7));
}
